import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle2, Download } from 'lucide-react';
import Button from '../ui/Button';
import { jsPDF } from 'jspdf';

interface CareerQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (responses: any) => void;
}

const questions = [
  {
    id: 'education',
    question: 'What is your highest level of completed education?',
    type: 'single',
    options: [
      { value: 'high_school', label: 'High School' },
      { value: 'bachelors', label: 'Bachelor\'s' },
      { value: 'masters', label: 'Master\'s' },
      { value: 'phd', label: 'PhD' }
    ]
  },
  {
    id: 'industries',
    question: 'Which industries are you most interested in exploring?',
    type: 'multi',
    options: [
      { value: 'technology', label: 'Technology' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'finance', label: 'Finance' },
      { value: 'education', label: 'Education' },
      { value: 'manufacturing', label: 'Manufacturing' },
      { value: 'retail', label: 'Retail' },
      { value: 'media', label: 'Media & Entertainment' },
      { value: 'consulting', label: 'Consulting' }
    ]
  },
  {
    id: 'career_goal',
    question: 'What is your primary career goal over the next 3 years?',
    type: 'single',
    options: [
      { value: 'technical_growth', label: 'Grow technically' },
      { value: 'leadership', label: 'Move into leadership' },
      { value: 'industry_switch', label: 'Switch industries' },
      { value: 'entrepreneurship', label: 'Start own business' }
    ]
  },
  {
    id: 'learning_comfort',
    question: 'How comfortable are you with learning new technologies and skills?',
    type: 'scale',
    min: 1,
    max: 5,
    labels: {
      1: 'Very Uncomfortable',
      3: 'Neutral',
      5: 'Very Comfortable'
    }
  },
  {
    id: 'current_skills',
    question: 'What are your top 3 current skills you feel confident about?',
    type: 'multi',
    maxSelect: 3,
    options: [
      { value: 'coding', label: 'Coding' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'problem_solving', label: 'Problem Solving' },
      { value: 'project_management', label: 'Project Management' },
      { value: 'data_analysis', label: 'Data Analysis' },
      { value: 'design', label: 'Design' },
      { value: 'communication', label: 'Communication' },
      { value: 'leadership', label: 'Leadership' }
    ]
  },
  {
    id: 'time_commitment',
    question: 'How many hours per week can you realistically dedicate to skill development?',
    type: 'single',
    options: [
      { value: '0-5', label: '0-5 hours' },
      { value: '5-10', label: '5-10 hours' },
      { value: '10-20', label: '10-20 hours' },
      { value: '20+', label: '20+ hours' }
    ]
  },
  {
    id: 'work_environment',
    question: 'What kind of work environment do you prefer?',
    type: 'single',
    options: [
      { value: 'remote', label: 'Remote' },
      { value: 'onsite', label: 'On-site' },
      { value: 'hybrid', label: 'Hybrid' }
    ]
  },
  {
    id: 'location_preference',
    question: 'Are you aiming for career opportunities locally or globally?',
    type: 'single',
    options: [
      { value: 'local', label: 'Local Country' },
      { value: 'global', label: 'Global' }
    ]
  },
  {
    id: 'learning_method',
    question: 'Which of the following learning methods do you prefer?',
    type: 'multi',
    options: [
      { value: 'self_paced', label: 'Self-paced courses' },
      { value: 'workshops', label: 'Live workshops' },
      { value: 'mentorship', label: 'Mentorship' },
      { value: 'certifications', label: 'Certifications' },
      { value: 'projects', label: 'Hands-on projects' }
    ]
  },
  {
    id: 'motivation',
    question: 'What motivates you the most in your career journey?',
    type: 'single',
    options: [
      { value: 'financial', label: 'Financial Growth' },
      { value: 'purpose', label: 'Purposeful Work' },
      { value: 'leadership', label: 'Leadership Opportunities' },
      { value: 'innovation', label: 'Innovation and Creativity' }
    ]
  }
];

const CareerQuestionnaireModal: React.FC<CareerQuestionnaireModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const handleResponse = (questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handleBack = () => {
    if (showSummary) {
      setShowSummary(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onComplete(responses);
    } finally {
      setLoading(false);
    }
  };

  const handleExportSummary = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.setTextColor(33, 64, 175);
    doc.text('Career Assessment Summary', 20, 20);
    
    let yPos = 40;
    
    // Add responses
    questions.forEach((question, index) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`${index + 1}. ${question.question}`, 20, yPos);
      yPos += 10;
      
      const response = responses[question.id];
      let responseText = '';
      
      if (Array.isArray(response)) {
        responseText = response.map(r => {
          const option = question.options.find(opt => opt.value === r);
          return option?.label || r;
        }).join(', ');
      } else if (question.type === 'scale') {
        responseText = `${response} - ${question.labels[response]}`;
      } else {
        const option = question.options.find(opt => opt.value === response);
        responseText = option?.label || response;
      }
      
      doc.setTextColor(100, 100, 100);
      doc.text(`Answer: ${responseText}`, 30, yPos);
      yPos += 20;
    });
    
    // Add date
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 280);
    
    doc.save('career-assessment-summary.pdf');
  };

  const currentQuestion = !showSummary ? questions[currentStep] : null;
  const hasAnswer = currentQuestion ? responses[currentQuestion.id] !== undefined : true;
  const isMultiSelect = currentQuestion?.type === 'multi';
  const isScale = currentQuestion?.type === 'scale';

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  };

  const getResponseLabel = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    const response = responses[questionId];
    
    if (!question || !response) return '';
    
    if (Array.isArray(response)) {
      return response.map(r => {
        const option = question.options.find(opt => opt.value === r);
        return option?.label || r;
      }).join(', ');
    } else if (question.type === 'scale') {
      return `${response} - ${question.labels[response]}`;
    } else {
      const option = question.options.find(opt => opt.value === response);
      return option?.label || response;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 sm:p-8"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              {showSummary ? (
                <>
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Assessment Complete!
                    </h2>
                    <p className="text-gray-600">
                      Here's a summary of your responses
                    </p>
                  </div>

                  <div className="max-h-[400px] overflow-y-auto mb-8">
                    {questions.map((question, index) => (
                      <div key={question.id} className="mb-6 last:mb-0">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <div>
                            <h3 className="text-gray-900 font-medium mb-1">
                              {question.question}
                            </h3>
                            <p className="text-gray-600">
                              {getResponseLabel(question.id)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleBack}
                        icon={<ArrowLeft className="h-4 w-4" />}
                      >
                        Back
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleExportSummary}
                        icon={<Download className="h-4 w-4" />}
                      >
                        Export Summary
                      </Button>
                    </div>
                    <Button
                      onClick={handleSubmit}
                      isLoading={loading}
                      icon={<ArrowRight className="h-4 w-4" />}
                      iconPosition="right"
                    >
                      Continue to Recommendations
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Progress bar */}
                  <div className="mb-8">
                    <div className="flex justify-between text-sm text-gray-500 mb-2">
                      <span>Question {currentStep + 1} of {questions.length}</span>
                      <span>{Math.round(((currentStep + 1) / questions.length) * 100)}% Complete</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-2 bg-primary-600 rounded-full transition-all duration-300"
                        style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Question */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      {currentQuestion.question}
                    </h2>

                    {/* Options */}
                    <div className="space-y-3">
                      {isScale ? (
                        <div className="flex justify-between items-center gap-4">
                          {Array.from({ length: currentQuestion.max - currentQuestion.min + 1 }).map((_, idx) => {
                            const value = currentQuestion.min + idx;
                            return (
                              <button
                                key={value}
                                onClick={() => handleResponse(currentQuestion.id, value)}
                                className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                                  responses[currentQuestion.id] === value
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="text-lg font-semibold mb-2">{value}</div>
                                {currentQuestion.labels[value] && (
                                  <div className="text-sm text-gray-500">
                                    {currentQuestion.labels[value]}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        currentQuestion.options.map(option => {
                          const isSelected = isMultiSelect
                            ? responses[currentQuestion.id]?.includes(option.value)
                            : responses[currentQuestion.id] === option.value;

                          const handleClick = () => {
                            if (isMultiSelect) {
                              const currentValues = responses[currentQuestion.id] || [];
                              if (isSelected) {
                                handleResponse(
                                  currentQuestion.id,
                                  currentValues.filter((v: string) => v !== option.value)
                                );
                              } else if (!currentQuestion.maxSelect || currentValues.length < currentQuestion.maxSelect) {
                                handleResponse(
                                  currentQuestion.id,
                                  [...currentValues, option.value]
                                );
                              }
                            } else {
                              handleResponse(currentQuestion.id, option.value);
                            }
                          };

                          return (
                            <button
                              key={option.value}
                              onClick={handleClick}
                              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              disabled={
                                isMultiSelect &&
                                currentQuestion.maxSelect &&
                                responses[currentQuestion.id]?.length >= currentQuestion.maxSelect &&
                                !isSelected
                              }
                            >
                              {option.label}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={currentStep === 0}
                      icon={<ArrowLeft className="h-4 w-4" />}
                    >
                      Back
                    </Button>

                    <Button
                      onClick={handleNext}
                      disabled={!hasAnswer}
                      icon={<ArrowRight className="h-4 w-4" />}
                      iconPosition="right"
                    >
                      {currentStep === questions.length - 1 ? 'Review Responses' : 'Next'}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CareerQuestionnaireModal;